/* eslint-disable prettier/prettier */
// Console Ninja's VS Code extension splices `/* c8 ignore start */ oo_cm() …`
// instrumentation into this file at runtime. Prettier sees the injected blob
// as malformed and blocks the dev-server overlay. The rule is disabled here
// because none of the formatting is hand-authored — `prettier --write` (and
// CI's `react-scripts build`) still enforces style on the parts we own.
import {Button, Collapse, Spin} from 'antd';
import {CaretRightOutlined, MessageOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import {GET_USER_CONTEXT} from 'graphql/queries/aiHelpdesk';
import {ticketCsatComment} from '../utils/csat';
import {CsatStars, csatTier} from '../components/CsatStars';
import {TicketUserAvatar} from '../components/TicketUserAvatar';
import {SelectionCards} from './contextCards/SelectionCards';
import styles from './ContextSidebar.module.scss';

const {Panel} = Collapse;

// Convert "PENDING_TRANSACTIONS" / "pending_transactions" → "Pending Transactions"
// and camelCase "pendingTransactions" → "Pending Transactions".
const humanize = input => {
  if (input === null || input === undefined || input === '') return '—';
  const str = String(input);
  // Skip humanization for things that clearly aren't enum-like (emails, URLs,
  // ISO timestamps, UUIDs, numbers stored as strings).
  if (/[@:/]/.test(str)) return str;
  if (/^\d+(\.\d+)?$/.test(str)) return str;
  return str
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
};

export const ContextSidebar = ({ticket}) => {
  const userId = ticket?.userId;
  const userProfileHref = userId
    ? `/dashboard/user/${userId}/overview`
    : undefined;

  const {data, loading} = useQuery(GET_USER_CONTEXT, {
    variables: {input: {userId: userId ? String(userId) : ''}},
    skip: !userId,
  });

  const userContext =
    data?.getUserContext?.__typename === 'ResponseWithUserContext'
      ? data.getUserContext.data
      : null;

  // ticket.contextBundle is the legacy aggregate snapshot — the SelectionCards
  // panel now sources its data from `events[].payload.contextData` instead,
  // which gives us per-selection cards with deep-link buttons.
  const flowTrace = ticket?.selectedOptions || [];

  const skillsUsed = Array.isArray(ticket?.skillsUsed) ? ticket.skillsUsed : [];
  const skillsUsedLabel = skillsUsed
    .map(
      s =>
        [s?.skill?.node, s?.skill?.tree].filter(Boolean).join(' / ') ||
        s?.skillId,
    )
    .filter(Boolean)
    .join(', ');

  const metadata = {
    Intent: ticket?.intent ? humanize(ticket.intent) : '—',
    Sentiment: ticket?.sentiment ? humanize(ticket.sentiment) : '—',
    'AI Turns': ticket?.aiTurnCount || 0,
    'Session Ref': ticket?.openclawSessionRef || '—',
    Created: ticket?.createdAt
      ? new Date(ticket.createdAt).toLocaleString()
      : '—',
    ...(ticket?.resolvingFaqPostId
      ? {'Resolved via FAQ': `#${ticket.resolvingFaqPostId}`}
      : {}),
    ...(skillsUsedLabel ? {'Used skills': skillsUsedLabel} : {}),
  };

  if (loading) return <Spin tip="Loading user context..." />;

  const userInfo = userContext?.user || {};
  const kyc = userContext?.kyc || {};
  const subscription = userContext?.subscription || {};

  // Identity (name / email / avatar) comes off the ticket's own user record —
  // GET_USER_CONTEXT doesn't carry those fields, but the ticket payload does.
  // Falls back to the unverified contactEmail for anonymous/unlinked tickets.
  const ticketUser = ticket?.user || {};
  const identityName = [ticketUser.firstName, ticketUser.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const identityEmail = ticketUser.email || ticket?.contactEmail || '';
  const identityDisplay = identityName || identityEmail || 'Customer';
  const identityInitials =
    `${(ticketUser.firstName || '').charAt(0)}${(
      ticketUser.lastName || ''
    ).charAt(0)}`.trim() || identityDisplay.charAt(0).toUpperCase();

  // Composite subscription block: { dabaPro { endDate, nextChargeDate }, simulator { active } }.
  // Daba Pro is entitled while endDate is still in the future; simulator carries
  // its own boolean.
  const dabaPro = subscription.dabaPro;
  const dabaProActive =
    !!dabaPro?.endDate && new Date(dabaPro.endDate).getTime() > Date.now();
  const planLabel = dabaProActive ? 'Daba Pro' : 'Basic';
  const simulatorActive = !!subscription.simulator?.active;

  // CSAT panel — pinned to the top of the sidebar. Show whenever the ticket
  // has either a score or a customer comment (`ticket.csatComment`, or the
  // legacy INTERNAL_NOTE with payload.source === 'csat'). Skip entirely when
  // there's neither.
  const csatComment = ticketCsatComment(ticket);
  const hasCsatScore =
    ticket?.csatScore !== null && ticket?.csatScore !== undefined;
  const showCsatPanel = hasCsatScore || !!csatComment;
  const csatPanelTier = csatTier(ticket?.csatScore);

  const renderCsatPanel = () => {
    if (!showCsatPanel) return null;
    const resolvedDisplay = ticket?.resolvedAt
      ? new Date(ticket.resolvedAt).toLocaleString()
      : null;
    return (
      <div
        className={`${styles.csatPanel} ${
          csatPanelTier === 'low' ? styles.csatPanelLow : ''
        }`}>
        <div className={styles.csatHeader}>
          <span className={styles.csatTitle}>Customer feedback</span>
          {resolvedDisplay ? (
            <span className={styles.csatResolvedAt}>
              resolved {resolvedDisplay}
            </span>
          ) : null}
        </div>
        <div className={styles.csatScoreRow}>
          <CsatStars score={ticket?.csatScore ?? null} size="lg" />
        </div>
        {csatComment?.text ? (
          <blockquote className={styles.csatQuote}>
            “{csatComment.text}”
          </blockquote>
        ) : hasCsatScore ? (
          <p className={styles.csatPlaceholder}>
            Customer rated but didn’t leave a comment.
          </p>
        ) : null}
        <div className={styles.csatFooter}>
          <span>
            ── customer · ticket TKT-
            {String(ticket?.id || '')
              .slice(0, 8)
              .toUpperCase()}
          </span>
        </div>
        {csatPanelTier === 'low' ? (
          <div className={styles.csatActions}>
            <Button
              type="primary"
              danger
              icon={<MessageOutlined />}
              onClick={() => {
                // Compose-dialog wiring isn't built yet — log the intent so
                // the eventual handler (or PostHog) can pick it up. Don't
                // auto-reopen the ticket; the customer chose to close it.
                if (typeof window !== 'undefined') {
                  // eslint-disable-next-line no-console
                  console.info('[csat] follow-up requested', {
                    ticketId: ticket?.id,
                    userId: ticket?.userId,
                    score: ticket?.csatScore,
                    comment: csatComment?.text || null,
                  });
                }
              }}>
              Follow up with customer
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.sidebarContainer}>
      {renderCsatPanel()}
      <Collapse
        defaultActiveKey={['1', '2', '3', '4']}
        ghost
        expandIcon={({isActive}) => (
          <CaretRightOutlined
            rotate={isActive ? 90 : 0}
            style={{color: '#94a3b8'}}
          />
        )}
        className={styles.customCollapse}>
        <Panel
          header={<span className={styles.panelTitle}>User Info</span>}
          key="1">
          <a
            className={`${styles.userIdentity} ${
              userId ? styles.userIdentityClickable : ''
            }`}
            href={userProfileHref}
            target={userId ? '_blank' : undefined}
            rel="noopener noreferrer"
            title={
              userId ? 'Open user profile' : 'No linked account for this ticket'
            }>
            <TicketUserAvatar
              imageUrl={ticketUser.imageUrl}
              initials={identityInitials}
              size="lg"
            />
            <div className={styles.userIdentityText}>
              <span className={styles.userIdentityName} title={identityDisplay}>
                {identityDisplay}
              </span>
              {identityEmail ? (
                <span
                  className={styles.userIdentityEmail}
                  title={identityEmail}>
                  {identityEmail}
                </span>
              ) : null}
            </div>
          </a>
          <div className={styles.infoGrid}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Country</span>
              <span className={styles.value}>
                {userInfo.country ? humanize(userInfo.country) : '—'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Language</span>
              <span className={styles.value}>
                {userInfo.language ? humanize(userInfo.language) : '—'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>KYC Status</span>
              <span className={styles.value}>
                {kyc.kycStatus ? humanize(kyc.kycStatus) : '—'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Plan</span>
              <span className={styles.value}>{planLabel}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Simulator</span>
              <span className={styles.value}>
                {simulatorActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </Panel>

        {flowTrace.length > 0 && (
          <Panel
            header={
              <span className={styles.panelTitle}>Guided Flow Trace</span>
            }
            key="2">
            <div className={styles.flowTrace}>
              {flowTrace.map((step, index) => (
                <div key={index} className={styles.traceStep}>
                  <span className={styles.traceIcon}>&#10003;</span>
                  <span className={styles.traceText}>{step}</span>
                </div>
              ))}
            </div>
          </Panel>
        )}

        <Panel
          header={<span className={styles.panelTitle}>Context</span>}
          key="3">
          <SelectionCards events={ticket?.events} />
        </Panel>

        <Panel
          header={<span className={styles.panelTitle}>Ticket Metadata</span>}
          key="4">
          <div className={styles.infoGrid}>
            {Object.entries(metadata).map(([key, val]) => (
              <div key={key} className={styles.infoRow}>
                <span className={styles.label}>{key}</span>
                <span className={styles.value}>{val}</span>
              </div>
            ))}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};
